import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import {
    Plus,
    SearchLg,
    Edit01,
    Users01
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { TableRowActionsDropdown } from '@/components/application/table/table';
import { accountService } from '@/data/mock-accounts';
import type { Account, AccountFilters, EntityType, AccountStatus, UserType } from '@/types/account';
import { useTranslation } from 'react-i18next';

export const AccountsListPage = () => {
    const { t } = useTranslation(['pages:accounts', 'common']);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<AccountStatus | ''>('');
    const [entityTypeFilter, setEntityTypeFilter] = useState<EntityType | ''>('');
    const [countryFilter, setCountryFilter] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState<UserType | ''>('');

    const entityTypeOptions = [
        { id: '', label: t('list.filters.allTypes') },
        { id: 'LLC', label: 'LLC' },
        { id: 'Corporation', label: 'Corporation' },
        { id: 'Partnership', label: 'Partnership' },
        { id: 'Sole Proprietorship', label: 'Sole Proprietorship' },
        { id: 'NGO', label: 'NGO' }
    ];

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'Attivo', label: t('list.status.active') },
        { id: 'Sospeso', label: t('list.status.suspended') },
        { id: 'Inattivo', label: t('list.status.inactive') }
    ];

    const countryOptions = [
        { id: '', label: t('list.filters.allCountries') },
        { id: 'United States', label: 'United States' },
        { id: 'United Kingdom', label: 'United Kingdom' },
        { id: 'Germany', label: 'Germany' },
        { id: 'Netherlands', label: 'Netherlands' },
        { id: 'France', label: 'France' },
        { id: 'Canada', label: 'Canada' },
        { id: 'Switzerland', label: 'Switzerland' }
    ];

    const userTypeOptions = [
        { id: '', label: t('list.filters.allTypes') },
        { id: 'SHIPPER', label: 'SHIPPER' },
        { id: 'RECEIVER', label: 'RECEIVER' }
    ];

    useEffect(() => {
        const loadAccounts = async () => {
            setIsLoading(true);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 500));

            const filters: AccountFilters = {
                search: searchTerm || undefined,
                status: statusFilter || undefined,
                entityType: entityTypeFilter || undefined,
                country: countryFilter || undefined,
                userType: userTypeFilter || undefined
            };

            const filteredAccounts = accountService.search(filters);
            setAccounts(filteredAccounts);
            setIsLoading(false);
        };

        loadAccounts();
    }, [searchTerm, statusFilter, entityTypeFilter, countryFilter, userTypeFilter]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'Attivo':
                return <Badge color="success" size="sm">{t('list.status.active')}</Badge>;
            case 'Sospeso':
                return <Badge color="warning" size="sm">{t('list.status.suspended')}</Badge>;
            case 'Inattivo':
                return <Badge color="error" size="sm">{t('list.status.inactive')}</Badge>;
            default:
                return <Badge color="gray" size="sm">{status}</Badge>;
        }
    };


    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('list.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <Link
                    to="/admin/accounts/new"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <Plus className="size-5" />
                    {t('list.newAccount')}
                </Link>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    <Input
                        placeholder={t('list.filters.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.filters.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as AccountStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.entityTypePlaceholder')}
                        items={entityTypeOptions}
                        selectedKey={entityTypeFilter}
                        onSelectionChange={(key) => setEntityTypeFilter(key as EntityType | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.countryPlaceholder')}
                        items={countryOptions}
                        selectedKey={countryFilter}
                        onSelectionChange={(key) => setCountryFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.userTypePlaceholder')}
                        items={userTypeOptions}
                        selectedKey={userTypeFilter}
                        onSelectionChange={(key) => setUserTypeFilter(key as UserType | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Accounts Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('list.table.title')} (${accounts.length})`}
                    description={t('list.table.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.table.headers.userType')} isRowHeader />
                        <Table.Head label={t('list.table.headers.legalName')} />
                        <Table.Head label={t('list.table.headers.entityType')} />
                        <Table.Head label={t('list.table.headers.country')} />
                        <Table.Head label={t('list.table.headers.status')} />
                        <Table.Head label={t('list.table.headers.createdAt')} />
                        <Table.Head label={t('list.table.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {accounts.map((account) => (
                            <Table.Row key={account.id}>
                                <Table.Cell>
                                    <Badge color={account.userType === 'SHIPPER' ? 'blue' : 'success'} size="sm">
                                        {account.userType}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <div>
                                        <div className="font-medium text-primary">{account.legalName}</div>
                                        <div className="text-sm text-tertiary">{account.email}</div>
                                        <div className="text-xs text-quaternary">{account.taxId}</div>
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{account.entityType}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{account.country}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    {getStatusBadge(account.status)}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {account.createdAt.toLocaleDateString('it-IT')}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/admin/accounts/${account.id}/edit`}
                                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            <Edit01 className="size-4" />
                                            {t('list.table.edit')}
                                        </Link>
                                        <TableRowActionsDropdown />
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {accounts.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <Users01 className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.table.noAccounts.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || statusFilter || entityTypeFilter || countryFilter || userTypeFilter
                                ? t('list.table.noAccounts.message')
                                : t('list.table.noAccounts.emptyMessage')
                            }
                        </p>
                    </div>
                    {!searchTerm && !statusFilter && !entityTypeFilter && !countryFilter && !userTypeFilter && (
                        <Link
                            to="/admin/accounts/new"
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                        >
                            <Plus className="size-5" />
                            {t('list.table.noAccounts.createFirst')}
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};
